import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import DesignSupportLayout from './designSupport/DesignSupportLayout'

const PAGE_SIZE = 50

export default function DesignSupportSurgerySearchPage() {
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const [cols, setCols] = useState({ g3: true, g5: true, g7: true, g8: true })
  const [focusTier, setFocusTier] = useState(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(String(q).trim()), 220)
    return () => clearTimeout(t)
  }, [q])

  useEffect(() => {
    setPage(1)
  }, [debounced])

  const fetchPage = useCallback(
    (pageNum) => {
      setLoading(true)
      setErr('')
      api
        .get('/operator/design-support/surgery-grades', {
          params: { q: debounced, page: pageNum, size: PAGE_SIZE },
        })
        .then((res) => {
          const data = res?.data?.data
          setRows(Array.isArray(data?.items) ? data.items : [])
          setTotal(Number(data?.total) || 0)
        })
        .catch(() => {
          setRows([])
          setTotal(0)
          setErr('목록을 불러오지 못했습니다.')
        })
        .finally(() => setLoading(false))
    },
    [debounced],
  )

  useEffect(() => {
    fetchPage(page)
  }, [fetchPage, page])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const toggleCol = (key) => {
    setCols((c) => ({ ...c, [key]: !c[key] }))
  }

  const tierHeaderClass = (tier) => {
    const on = focusTier === tier
    return `px-3 py-2 w-24 whitespace-nowrap transition ${on ? 'bg-sky-50 text-brand-navy ring-1 ring-brand-sky/40' : ''}`
  }

  return (
    <DesignSupportLayout>
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <h2 className="text-lg font-semibold text-slate-900">수술명 · 종수술비 참고</h2>
          <Link to="/design/disease-code" className="shrink-0 text-xs font-medium text-brand-sky hover:underline sm:text-sm">
            ← 질병코드 · 특약
          </Link>
        </div>

        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-950 space-y-2">
          <p>
            본 화면은 <strong>보험사 약관을 대신하거나 보장을 확정하지 않습니다.</strong> DB(
            <code className="text-xs">work_surgery_grade_row</code>)에 적재된 참고 등급만 표시합니다.{' '}
            <strong>실제 지급은 해당 계약 약관·보험사 안내가 우선</strong>입니다.
          </p>
        </div>

        <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-medium text-slate-600">표시 열</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {[
                { key: 'g3', label: '1~3종' },
                { key: 'g5', label: '1~5종' },
                { key: 'g7', label: '1~7종' },
                { key: 'g8', label: '1~8종' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                    cols[key] ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'border-slate-200 bg-slate-50 text-slate-500 line-through'
                  }`}
                  onClick={() => toggleCol(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600">열 강조(참고)</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {[
                { id: '3', label: '1~3 체계' },
                { id: '5', label: '1~5 체계' },
                { id: '7', label: '1~7 체계' },
                { id: '8', label: '1~8 체계' },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                    focusTier === t.id ? 'border-sky-500 bg-sky-50 text-brand-navy' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  onClick={() => setFocusTier((v) => (v === t.id ? null : t.id))}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-slate-700">수술명 검색</label>
          <input
            className="mt-1 w-full max-w-xl rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="키워드 (비우면 전체 첫 페이지)"
          />
          {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
          <p className="mt-2 text-xs text-slate-500">
            {loading ? '불러오는 중…' : `전체 ${total.toLocaleString()}건 · 페이지 ${page} / ${totalPages}`}
          </p>
        </div>

        <div className="mt-4 overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-600">
              <tr>
                <th className="px-3 py-2">수술명</th>
                {cols.g3 && <th className={tierHeaderClass('3')}>1~3종</th>}
                {cols.g5 && <th className={tierHeaderClass('5')}>1~5종</th>}
                {cols.g7 && <th className={tierHeaderClass('7')}>1~7종</th>}
                {cols.g8 && <th className={tierHeaderClass('8')}>1~8종</th>}
                <th className="px-3 py-2 min-w-[8rem]">출처 메모</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={1 + [cols.g3, cols.g5, cols.g7, cols.g8].filter(Boolean).length + 1}
                    className="px-3 py-10 text-center text-slate-500"
                  >
                    {loading ? '불러오는 중…' : '데이터가 없습니다. 데이터 적재에서 수술 CSV를 넣은 뒤 다시 조회하세요.'}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-900">{r.surgeryName}</td>
                    {cols.g3 && (
                      <td className={`px-3 py-2 text-xs ${focusTier === '3' ? 'bg-sky-50/60' : ''}`}>{r.grade3 || '—'}</td>
                    )}
                    {cols.g5 && (
                      <td className={`px-3 py-2 text-xs ${focusTier === '5' ? 'bg-sky-50/60' : ''}`}>{r.grade5 || '—'}</td>
                    )}
                    {cols.g7 && (
                      <td className={`px-3 py-2 text-xs ${focusTier === '7' ? 'bg-sky-50/60' : ''}`}>{r.grade7 || '—'}</td>
                    )}
                    {cols.g8 && (
                      <td className={`px-3 py-2 text-xs ${focusTier === '8' ? 'bg-sky-50/60' : ''}`}>{r.grade8 || '—'}</td>
                    )}
                    <td className="px-3 py-2 text-xs text-slate-600">{r.dataSourceNote || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center gap-3 text-sm">
            <button
              type="button"
              className="rounded border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              이전
            </button>
            <span className="text-slate-600">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              className="rounded border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              다음
            </button>
          </div>
        )}
      </div>
    </DesignSupportLayout>
  )
}
